import { Input } from '@klaro7/ui';

import type { SignedReceiptStatusValue } from '@/features/fiscal/api';

// Stesso stile dei filtri della lista utenti admin.
export const FILTER_SELECT_CLS =
  'h-9 rounded-lg border border-neutral-200 bg-white px-3 text-sm text-neutral-900 focus:border-brand-teal/50 focus:outline-none';

export function SearchFilterInput({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}) {
  return (
    <div className="relative w-full sm:w-72">
      <Input
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        aria-label={placeholder}
        className="h-9 pr-9"
        leftIcon={(
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
          </svg>
        )}
      />
      {value && (
        <button
          type="button"
          onClick={() => onChange('')}
          className="absolute right-2 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-md text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-600"
          aria-label="Cancella ricerca"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
          </svg>
        </button>
      )}
    </div>
  );
}

export function DateRangeFilter({
  from,
  to,
  onChange,
}: {
  from: string;
  to: string;
  onChange: (patch: { from?: string; to?: string }) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-1.5 text-sm text-neutral-500">
      <label className="flex items-center gap-1.5">
        <span>Periodo dal</span>
        <input
          type="date"
          value={from}
          max={to || undefined}
          onChange={(e) => onChange({ from: e.target.value })}
          className={FILTER_SELECT_CLS}
        />
      </label>
      <label className="flex items-center gap-1.5">
        <span>al</span>
        <input
          type="date"
          value={to}
          min={from || undefined}
          onChange={(e) => onChange({ to: e.target.value })}
          className={FILTER_SELECT_CLS}
        />
      </label>
    </div>
  );
}

export function ResetFiltersButton({ onClick, disabled }: { onClick: () => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="h-9 rounded-lg border border-neutral-200 bg-white px-3 text-sm text-neutral-600 transition-colors hover:bg-neutral-50 disabled:cursor-not-allowed disabled:opacity-40"
    >
      Azzera filtri
    </button>
  );
}

const RECEIPT_STATUS_UI: Record<SignedReceiptStatusValue, { label: string; cls: string }> = {
  missing: { label: 'Mancante', cls: 'bg-neutral-100 text-neutral-600' },
  uploaded: { label: 'Caricata', cls: 'bg-amber-100 text-amber-700' },
  verified: { label: 'Verificata', cls: 'bg-green-100 text-green-700' },
};

/** Select del filtro sullo stato della ricevuta per ritenuta firmata. */
export function ReceiptStatusSelect({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      aria-label="Filtra per ritenuta firmata"
      className={FILTER_SELECT_CLS}
    >
      <option value="">Ritenuta firmata: tutte</option>
      <option value="missing">Ritenuta firmata mancante</option>
      <option value="uploaded">Ritenuta firmata da verificare</option>
      <option value="verified">Ritenuta firmata verificata</option>
    </select>
  );
}

/** Badge dello stato della ricevuta per ritenuta firmata. */
export function SignedReceiptBadge({ status }: { status: SignedReceiptStatusValue | null | undefined }) {
  if (!status) return null;
  const { label, cls } = RECEIPT_STATUS_UI[status];
  return (
    <span
      className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ${cls}`}
      title="Ricevuta per ritenuta d'acconto firmata"
    >
      Firmata: {label}
    </span>
  );
}
